<template>
  <div>
    <div v-if="availabilities.length" class="my-5">
      <div class="products-list my-3">
        <div
          v-for="availability in displayAvailabilities"
          :key="roomKey(availability)"
          class="mx-0 products-list-item mb-3"
        >
          <div class="row px-md-3 px-1 py-md-4 py-3">
            <div class="col-md-5">
              <div class="position-relative hotel-slider">
                <b-carousel
                  :id="`cultswitch-carousel-${roomKey(availability)}`"
                  :interval="3000"
                  style="text-shadow: 0px 0px 2px #000"
                  fade
                  indicators
                  img-widths="600"
                  img-heights="400"
                  ><div class="cursor-pointer" @click="$emit('openDetails', cultswitchRoomImageUrls(availability))">
                    <template v-if="cultswitchRoomImages(availability).length">
                      <div
                        v-for="item of cultswitchRoomImages(availability)"
                        :key="`${roomKey(availability)}-${item.id}`"
                      >
                        <b-carousel-slide
                          :img-src="item.url"
                        ></b-carousel-slide>
                      </div>
                    </template>
                    <template v-else>
                      <b-carousel-slide
                        v-for="i of 1"
                        :key="i"
                        img-src="/images/property-default-image.jpg"
                      ></b-carousel-slide>
                    </template>
                  </div>
                </b-carousel>
              </div>
            </div>
            <div class="col-md-7">
              <h3 class="mb-3 text-main font-weight-bold mt-md-0 mt-3">
                {{ availability.name }}
              </h3>
              <div
                v-if="availability.description"
                class="product-desc"
                :class="{ 'product-desc--clamped': !showAll }"
                :style="{ '--max-lines': currentConfigKey('productDescriptionLimit') }"
                :ref="`description-${roomKey(availability)}`"
                v-html="availability.description"
              ></div>
              <span
                v-if="availability.description && showToggle(availability)"
                @click="toggleDescription"
                class="text-main cursor-pointer small"
                ><u>{{
                  $t(`property.${showAll ? "showLess" : "showMore"}`)
                }}</u></span
              >
            </div>
          </div>
          <div
            v-for="product in availability.products"
            :key="product.id"
            class="selectors p-md-3 p-1 py-3"
          >
            <div
              v-for="(guest, index) in productGuests(product)"
              :key="`${product.id}-${index}`"
            >
              <div class="row mb-2">
                <div v-if="index === 0" class="col-md-5 col-9">
                  <div class="small d-md-flex d-block align-items-center font-weight-bold text-success">
                    <div
                      :class="`text-${
                        product.mealsIncluded ? 'success' : 'danger'
                      }`"
                    >
                      <template v-if="product.mealsIncluded">
                        <img
                          src="../../assets/icons/meal-success.svg"
                          class="mr-1"
                          alt="meal"
                        />
                        <span>
                          {{ getTextTranslation("meals", product.mealsIncluded) }}
                          {{ $t("property.included") }}
                        </span>
                      </template>
                      <template v-else>
                        <img
                          src="../../assets/icons/meal.svg"
                          class="mr-1"
                          alt="meal"
                        />
                        <span>{{ $t("property.noMealIncluded") }}</span>
                      </template>
                    </div>
                    <template v-if="payableAtProperty(product)">
                      <span class="px-2 d-md-inline d-none">|</span>
                      <span class="d-md-inline d-block">
                        <b-icon icon="wallet2" class="mr-1"></b-icon> {{ $t("property.payAtProperty") }}
                      </span>
                    </template>
                  </div>
                  <div class="mt-2 font-weight-bold">
                    {{ product.productDescription.name }}
                  </div>
                  <div class="text-main mt-2 policies">
                    <span
                      role="button"
                      v-b-popover.bottom.hover.html="rateDescription(product)"
                      >{{ $t("property.rateDetails") }}</span
                    >
                    <span class="px-1">|</span>
                    <span
                      role="button"
                      v-b-popover.bottom.hover.html="
                        cancellationPolicyText(product)
                      "
                      >{{ $t("property.cancellationPolicy") }}</span
                    >
                    <span class="px-1" v-if="product.paymentPolicy">|</span>
                    <span
                      v-if="product.paymentPolicy"
                      role="button"
                      v-b-popover.bottom.hover.html="paymentPolicyText(product)"
                      >{{ $t("property.paymentPolicy") }}</span
                    >
                  </div>
                </div>
                <div v-if="index === 0" class="d-md-none d-block col-3">
                  <div class="text-14 text-right">
                    {{ $t("property.roomOrRooms") }}
                  </div>
                  <Multiselect
                    placeholder=""
                    style="width: 70px"
                    class="mt-"
                    :options="cultswitchRoomsOption(product)"
                    :showLabels="false"
                    :searchable="false"
                    :allow-empty="false"
                    @select="cultswitchCartChanged($event, product)"
                    :value="productCart(product.id)?.quantity || 0"
                  />
                </div>
                <div
                  class="col-md-3 col-6 mt-md-0 mt-2"
                  :class="{ 'offset-md-5': index > 0 }"
                >
                  <div v-if="index === 0" class="text-14">
                    {{ $t("property.persons") }}
                  </div>
                  <div class="d-flex align-items-start mt-md-3 mt-1">
                    <div>
                      <Multiselect
                        placeholder=""
                        style="width: 100px"
                        class="mr-3"
                        :options="cultswitchPersonsOption(product)"
                        :showLabels="false"
                        :searchable="false"
                        :allow-empty="false"
                        @select="clearVariationSelection(product)"
                        v-model="guest.guestCount"
                      >
                        <template slot="singleLabel" slot-scope="props">
                          <GuestIcons :persons="props.option" />
                        </template>
                        <template slot="option" slot-scope="props">
                          <GuestIcons :persons="props.option" />
                        </template>
                      </Multiselect>
                      <div class="mt-1 text-14">
                        {{
                          $t("property.maxPersons", {
                            count: product.productPriceVariation.maxGuest,
                          })
                        }}
                      </div>
                    </div>
                    <div class="text-14">{{ $t("property.adults") }}</div>
                  </div>
                </div>
                <div
                  class="col-md-4 col-6 mt-md-0 mt-2 text-md-left text-right"
                >
                  <div
                    class="d-md-flex d-block align-items-start justify-content-between"
                  >
                    <div>
                      <div v-if="index === 0" class="text-14">
                        {{
                          $t(
                            `property.pricePer${
                              stayMode ? "Stay" : "RoomPerNight"
                            }`
                          )
                        }}
                      </div>
                      <div class="mt-md-2 mt-1">
                        <div
                          class="strike discounted-amount"
                          v-if="
                            cultswitchProductPrice(
                              product,
                              guest.guestCount,
                              'discountAmount'
                            ) > 0
                          "
                        >
                          {{ product.currencyCode }}
                          {{
                            cultswitchProductPrice(
                              product,
                              guest.guestCount,
                              "actualAmount"
                            ) | formatPrice
                          }}
                        </div>
                        <h3 class="text-main font-weight-bold actual-amount">
                          {{ product.currencyCode }}
                          {{
                            cultswitchProductPrice(product, guest.guestCount)
                              | formatPrice
                          }}
                        </h3>
                        <div
                          class="discounted-amount"
                          v-if="
                            cultswitchProductPrice(
                              product,
                              guest.guestCount,
                              'discountAmount'
                            ) > 0
                          "
                        >
                          {{ $t("property.youSave") }}:
                          <span class="text-main">
                            {{ product.currencyCode }}
                            {{
                              cultswitchProductPrice(
                                product,
                                guest.guestCount,
                                "discountAmount"
                              )
                            }}
                            ({{
                              cultswitchProductPrice(
                                product,
                                guest.guestCount,
                                "discountPercent",
                                0
                              )
                            }}%)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-if="index === 0" class="d-none d-md-block">
                      <div>{{ $t("property.roomOrRooms") }}</div>
                      <Multiselect
                        placeholder=""
                        style="width: 70px"
                        class="mt-1"
                        :options="cultswitchRoomsOption(product)"
                        :showLabels="false"
                        :searchable="false"
                        :allow-empty="false"
                        @select="cultswitchCartChanged($event, product)"
                        :value="productCart(product.id)?.quantity || 0"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-for="(variation, idx) in guest.variations"
                :key="`${variation.minAge}-${index}-${idx}`"
              >
                <div class="row">
                  <div class="col-md-3 offset-md-5 col-6">
                    <div class="d-flex align-items-start mt-3">
                      <div>
                        <Multiselect
                          placeholder=""
                          style="width: 100px"
                          class="mr-3"
                          :options="
                            variationOption(
                              product,
                              guest.guestCount,
                              guest.variations
                            )
                          "
                          :showLabels="false"
                          :searchable="false"
                          :allow-empty="false"
                          @select="
                            variationSelected($event, product, index, idx)
                          "
                          :value="variation.quantity"
                        />
                      </div>
                      <div
                        class="mt-md-2 mt-0 text-14"
                        :class="{
                          'custom-opacity-45': variation.quantity <= 0,
                        }"
                      >
                        {{ $t("property.children") }} ({{ variation.minAge }} -
                        {{ variation.maxAge }})
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4 col-6 text-md-left text-right">
                    <div
                      class="mt-3"
                      :class="{ 'custom-opacity-45': variation.quantity <= 0 }"
                    >
                      <h3 class="text-main font-weight-bold actual-amount">
                        {{ product.currencyCode }}
                        {{ variation.totalAmount | formatPrice }}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="index < productGuests(product).length - 1"
                class="offset-md-5 col-md-6 mt-4"
              >
                <hr />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="fetched && !availabilities.length"
      class="text-danger no-availability py-3 my-5 text-center"
    >
      <h5 class="mb-0">
        <b-icon icon="exclamation-circle" class="text-danger"></b-icon>
        <span class="ml-2">
          <span v-html="noAvailabilityMessage"></span>
          <span v-if="promoCode">{{ $t("property.andPromo") }}</span>
        </span>
      </h5>
    </div>
    <div v-else-if="!fetched" class="py-5 mt-5 text-center text-md">
      <h5>{{ $t("property.selectStay") }}</h5>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import GuestIcons from "../Widgets/GuestIcons.vue";
import {
  rateDescription,
  cancellationPolicyText,
  paymentPolicyText,
} from "../../helpers/cultswitch";
import { currentConfigKey, getNoAvailabilityMessage } from "../../helpers/theme";
import TranslationHelper from "../../helpers/translation";
import { mapState } from "vuex";
export default {
  name: "CultswitchList",
  props: {
    availabilities: {
      type: Array,
      required: true,
    },
    stayMode: {
      type: Boolean,
      required: true,
    },
    fetched: {
      type: Boolean,
      default: false,
    },
    checkIn: {
      type: String,
      default: null,
    },
    checkOut: {
      type: String,
      default: null,
    },
    promoCode: {
      type: String,
      default: "",
    },
    hotelEmail: {
      type: String,
      default: "",
    },
    guests: {
      type: Number,
      default: 2,
    },
    rooms: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      cart: [],
      overflowingDescriptions: {},
      showAll: false,
    };
  },
  components: {
    GuestIcons,
    Multiselect,
  },
  computed: {
    ...mapState("property", ["availabiltyDefaultMedia"]),
    noAvailabilityMessage() {
      return getNoAvailabilityMessage({
        checkIn: this.checkIn,
        checkOut: this.checkOut,
        hotelEmail: this.hotelEmail,
      });
    },
    displayAvailabilities() {
      return (this.availabilities || []).map((availability) => ({
        ...availability,
        media: this.cultswitchRoomImages(availability),
      }));
    },
  },
  methods: {
    rateDescription,
    currentConfigKey,
    paymentPolicyText,
    cancellationPolicyText,
    getTextTranslation: TranslationHelper.getTextTranslation,
    toggleDescription() {
      this.showAll = !this.showAll;
      this.updateDescriptionOverflow();
    },
    updateDescriptionOverflow() {
      this.$nextTick(() => {
        const overflowingDescriptions = {};
        this.availabilities.forEach((availability) => {
          const refKey = `description-${this.roomKey(availability)}`;
          const elementRef = this.$refs[refKey];
          const element = Array.isArray(elementRef) ? elementRef[0] : elementRef;
          if (!element) {
            return;
          }

          const wasClamped = element.classList.contains("product-desc--clamped");
          if (!wasClamped) {
            element.classList.add("product-desc--clamped");
          }

          const key = this.roomKey(availability);
          overflowingDescriptions[key] =
            element.scrollHeight > element.clientHeight + 1;

          if (!wasClamped) {
            element.classList.remove("product-desc--clamped");
          }
        });

        this.overflowingDescriptions = overflowingDescriptions;
      });
    },
    showToggle(availability) {
      return !!this.overflowingDescriptions[this.roomKey(availability)];
    },
    payableAtProperty(product) {
      const { bookingGuaranteeCode, paymentPolicy } = product;
      return !paymentPolicy && bookingGuaranteeCode && bookingGuaranteeCode.toString() !== "3";
    },
    roomKey(availability) {
      return availability?.code || availability?.id;
    },
    cultswitchRoomImages(availability) {
      const sourceMedia = Array.isArray(availability?.media) ? availability.media : [];
      const seenUrls = new Set();

      const images = sourceMedia
        .filter((image) => {
          if (!image?.url || seenUrls.has(image.url)) {
            return false;
          }
          seenUrls.add(image.url);
          return true;
        })
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

      if (images.length || !this.availabiltyDefaultMedia?.length) {
        return images;
      }

      const roomKey = this.roomKey(availability);
      return this.availabiltyDefaultMedia.map((url, index) => ({
        id: `default-${roomKey}-${index}`,
        url,
        sortOrder: index,
      }));
    },
    cultswitchRoomImageUrls(availability) {
      const imgs = this.cultswitchRoomImages(availability);
      return imgs.map(({ url }) => url);
    },
    productCart(productId) {
      return this.cart.find(({ id }) => id === productId);
    },
    cultswitchPersonsOption(product) {
      const { minGuest, maxGuest } = product.productPriceVariation;
      return Array.from(
        { length: maxGuest - minGuest + 1 },
        (_, i) => minGuest + i
      );
    },
    variationOption(product, guests, variations) {
      let totalSelected = guests;
      variations.forEach(({ quantity }) => {
        totalSelected += quantity;
      });
      const { minGuest, maxGuest } = product.productPriceVariation;
      const available = maxGuest - totalSelected;
      if (available < minGuest) {
        return [0];
      }

      return Array.from({ length: available + 1 }, (_, index) => index);
    },
    productGuests(product) {
      const item = this.productCart(product.id);
      if (!item) {
        const data = {
          id: product.id,
          product,
          quantity: 0,
          guests: [
            {
              guestCount: product.productPriceVariation.minGuest || 1,
              price: this.cultswitchProductPrice(
                product,
                product.productPriceVariation.minGuest || 1
              ),
              variations: this.priceVariations(
                product,
                product.productPriceVariation.minGuest || 1
              ),
            },
          ],
          currencyCode: product.currencyCode,
        };
        this.cart.push(data);
      }
      return item?.guests;
    },
    cultswitchRoomsOption(product) {
      return Array.from({ length: product.numberOfUnits + 1 }, (_, i) => i);
    },
    cultswitchProductPrice(
      product,
      guests = 1,
      key = "totalAmount",
      precision = 2
    ) {
      let prices = product.roomRates;
      if (prices[guests.toString()]) {
        return prices[guests.toString()][key].toFixed(precision);
      }
      prices = Object.values(prices);
      return (prices.length ? prices[0][key] : 0).toFixed(precision);
    },
    priceVariations(product, guests = 1) {
      const variations = product.productPriceVariation.priceVariation.filter(
        ({ maxAge }) => maxAge > 0
      );
      return variations
        .map((variation) => {
          variation.totalAmount = variation.value;
          variation.quantity = 0;
          if (variation.isPercent) {
            variation.totalAmount =
              (variation.value / 100) *
              this.cultswitchProductPrice(product, guests, "totalAmount", 0);
          }
          variation.totalAmount =
            (variation.totalAmount + variation.extraTotal) *
            variation.multiplier;
          return variation;
        })
        .sort((a, b) => a.minAge - b.minAge);
    },
    clearVariationSelection(product) {
      const idx = this.cart.findIndex(({ id }) => id === product.id);
      if (idx !== -1) {
        this.cart[idx].guests.forEach((guest) => {
          guest.variations = this.priceVariations(product, guest.guestCount);
        });
      }
    },
    variationSelected(value, product, guestIdx, varIdx) {
      const idx = this.cart.findIndex(({ id }) => id === product.id);
      if (idx !== -1) {
        const updatedCartItem = { ...this.cart[idx] };
        updatedCartItem.guests[guestIdx].variations[varIdx].quantity = value;
        this.cart.splice(idx, 1, updatedCartItem);
      }
    },
    cultswitchCartChanged(value, product) {
      const idx = this.cart.findIndex(({ id }) => id === product.id);
      const guestCount = product.productPriceVariation?.minGuest || 1;

      if (idx === -1) {
        if (value > 0) {
          const data = {
            id: product.id,
            product,
            quantity: value,
            guests: [],
            currencyCode: product.currencyCode,
          };
          for (let i = 0; i < value; i++) {
            data.guests.push({
              guestCount,
              price: this.cultswitchProductPrice(product, guestCount),
              variations: this.priceVariations(product, guestCount),
            });
          }
          this.cart.push(data);
        }
      } else {
        if (value === 0) {
          this.cart.splice(idx, 1);
        } else {
          const existingGuests = this.cart[idx].guests || [];
          const updatedGuests = existingGuests.slice(0, value).map((guest) => ({
            ...guest,
            price: this.cultswitchProductPrice(product, guest.guestCount),
            variations: this.priceVariations(product, guest.guestCount).map(
              (variation) => {
                const selectedVariation = guest.variations?.find(
                  (item) =>
                    item.minAge === variation.minAge &&
                    item.maxAge === variation.maxAge
                );
                return {
                  ...variation,
                  quantity: selectedVariation?.quantity || 0,
                };
              }
            ),
          }));

          this.cart[idx].quantity = value;
          this.cart[idx].product = product;

          for (let i = updatedGuests.length; i < value; i++) {
            updatedGuests.push({
              guestCount,
              price: this.cultswitchProductPrice(product, guestCount),
              variations: this.priceVariations(product, guestCount),
            });
          }

          this.cart[idx].guests = updatedGuests;
        }
      }
    },
  },
  watch: {
    showAll() {
      this.updateDescriptionOverflow();
    },
    availabilities: {
      handler(availabilities) {
        availabilities.forEach((availability) => {
          availability.products?.forEach((product) => {
            this.clearVariationSelection(product);
          });
        });
        this.updateDescriptionOverflow();
      },
      deep: true,
      immediate: true,
    },
    availabiltyDefaultMedia() {
      this.updateDescriptionOverflow();
    },
    cart: {
      handler(updatedCart) {
        updatedCart.forEach((item, idx) => {
          item.guests.forEach((guest, idxx) => {
            this.cart[idx].guests[idxx].price = this.cultswitchProductPrice(
              item.product,
              guest.guestCount
            );
          });
        });
        const localCart = JSON.parse(JSON.stringify(this.cart));
        this.$emit(
          "cartUpdated",
          localCart.filter((item) => item.quantity > 0)
        );
      },
      deep: true,
    },
  },
  mounted() {
    window.addEventListener("resize", this.updateDescriptionOverflow);
    this.updateDescriptionOverflow();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateDescriptionOverflow);
  },
};
</script>

<style>
.product-desc--clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--max-lines, 4);
  line-clamp: var(--max-lines, 4);
  overflow: hidden;
}

.strike {
  text-decoration: line-through;
}
.text-14 {
  font-size: 14px;
}
.discounted-amount {
  font-size: 14px;
}
.custom-opacity-45 {
  opacity: 0.4 !important;
}
.actual-amount {
  font-size: 28px;
}
@media screen and (max-width: 600px) {
  .discounted-amount {
    font-size: 12px;
  }
  .actual-amount {
    font-size: 20px;
    margin-bottom: 0;
  }
  .text-14 {
    font-size: 12px;
  }
}
</style>
